import React, { useCallback, useEffect, useState } from 'react';

/**
 * Platform Console → Global Settings → WhatsApp.
 *
 * The PLATFORM half of the WhatsApp channel: one vendor account (CERF) shared
 * by every property, plus the master switch. Each hotel then enters its own
 * WhatsApp Business number and manages its own templates in
 * Hotel Config → WhatsApp.
 *
 * The API token is write-only — the server returns only whether one is stored.
 * See docs/SRS_WHATSAPP_CERF_INTEGRATION.md §12.1.
 */

const CERF_DEFAULT_URL = 'https://sbcarmsoft.cerfsolutions.com/api/whatsapp/msg';

export default function WhatsAppPlatformSettings({ token }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [existing, setExisting] = useState(null);

  const [form, setForm] = useState({
    display_name: 'Cerf',
    base_url: CERF_DEFAULT_URL,
    api_token: '',
    sender_phone: '',
    is_active: true,
    is_enabled_all_properties: false,
  });
  const [tokenEditing, setTokenEditing] = useState(false);

  // Test panel
  const [testOpen, setTestOpen] = useState(false);
  const [testMobile, setTestMobile] = useState('');
  const [testTemplateId, setTestTemplateId] = useState('');
  const [testParams, setTestParams] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );
  const clearBanners = () => { setError(''); setMessage(''); };

  const load = useCallback(async () => {
    setError(''); setMessage('');
    setLoading(true);
    try {
      const res = await fetch('/api/admin/whatsapp-provider', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load WhatsApp configuration');
      setExisting(data);
      if (data) {
        setForm({
          display_name: data.display_name || 'Cerf',
          base_url: data.base_url || CERF_DEFAULT_URL,
          api_token: '',
          sender_phone: data.sender_phone || '',
          is_active: data.is_active !== false,
          is_enabled_all_properties: Boolean(data.is_enabled_all_properties),
        });
      }
      setTokenEditing(!data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    clearBanners();
    if (!form.base_url.trim()) { setError('API Endpoint is required.'); return; }
    if (!existing && !form.api_token.trim()) { setError('API Token is required on first save.'); return; }
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name,
        base_url: form.base_url.trim(),
        sender_phone: form.sender_phone,
        is_active: form.is_active,
        is_enabled_all_properties: form.is_enabled_all_properties,
      };
      // Blank token = leave the stored one alone.
      if (form.api_token.trim()) payload.api_token = form.api_token.trim();
      const res = await fetch('/api/admin/whatsapp-provider', {
        method: 'PUT', headers: headers(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage('WhatsApp configuration saved.');
      setForm(f => ({ ...f, api_token: '' }));
      setTokenEditing(false);
      setExisting(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTestResult(null);
    if (!testMobile.trim() || !testTemplateId.trim()) {
      setTestResult({ success: false, error_message: 'Mobile and Template ID are both required.' });
      return;
    }
    setTestRunning(true);
    try {
      const params = testParams.split('|').map(s => s.trim()).filter(s => s.length > 0);
      const res = await fetch('/api/admin/whatsapp-provider/test', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ mobile: testMobile.trim(), template_id: testTemplateId.trim(), params }),
      });
      const data = await res.json();
      setTestResult(res.ok ? data : { success: false, error_message: data?.error || 'Test failed' });
    } catch (err) {
      setTestResult({ success: false, error_message: err.message });
    } finally {
      setTestRunning(false);
    }
  };

  const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '1rem 1.25rem', marginBottom: '1rem' };
  const label = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' };
  const btn = { padding: '0.45rem 0.9rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
  const hint = { fontSize: '0.72rem', color: '#64748b', marginTop: 3 };

  return (
    <div style={{ padding: '1.25rem', maxWidth: 900 }}>
      <h2 style={{ margin: '0 0 0.25rem', color: '#1e3a8a' }}>WhatsApp Settings</h2>
      <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Configure the platform-level WhatsApp vendor. All properties send through this provider.
        Each hotel enters its own WhatsApp Business number and manages its own message templates
        in Hotel Config → WhatsApp.
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>{error}</div>}
      {message && <div style={{ ...card, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>{message}</div>}

      <div style={card}>
        <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '0.2rem' }}>💬 WhatsApp Provider</div>
        <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '0.9rem' }}>
          Secret keys are write-only and never echoed back.
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.9rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_enabled_all_properties}
                 onChange={e => setForm(f => ({ ...f, is_enabled_all_properties: e.target.checked }))} />
          <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1f2937' }}>Enable WhatsApp messaging for all properties</span>
        </label>
        <div style={{ ...hint, marginTop: -8, marginBottom: '0.9rem' }}>
          Master switch. While this is off, no property sends WhatsApp messages regardless of its own settings.
        </div>

        <div style={{ marginBottom: '0.9rem' }}>
          <div style={label}>Provider / Vendor</div>
          <div style={{
            display: 'inline-block', padding: '0.5rem 1.4rem', borderRadius: 8,
            border: '2px solid #2563eb', background: '#eff6ff', color: '#1e40af', fontWeight: 700, fontSize: '0.9rem',
          }}>Cerf</div>
          <div style={hint}>CERF is the only supported vendor today.</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem' }}>
          <div>
            <label style={label}>API Token</label>
            {existing?.api_token_set && !tokenEditing ? (
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input value="••••••••••" readOnly style={{ ...inputStyle, background: '#f1f5f9', color: '#64748b' }} />
                <button onClick={() => setTokenEditing(true)}
                        style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe', whiteSpace: 'nowrap' }}>Change</button>
              </div>
            ) : (
              <input type="password" value={form.api_token} autoComplete="new-password"
                     onChange={e => setForm(f => ({ ...f, api_token: e.target.value }))}
                     placeholder={existing?.api_token_set ? 'Enter a new token to replace' : 'Bearer token from CERF'}
                     style={inputStyle} />
            )}
            <div style={hint}>Stored write-only. Leave blank to keep the current token.</div>
          </div>

          <div>
            <label style={label}>Sender Phone</label>
            <input value={form.sender_phone}
                   onChange={e => setForm(f => ({ ...f, sender_phone: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                   placeholder="919943018468" style={inputStyle} />
            <div style={hint}>Registered WhatsApp sender number (no +). Used only as a fallback when a property has not set its own.</div>
          </div>

          <div>
            <label style={label}>API Endpoint</label>
            <input value={form.base_url}
                   onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))}
                   placeholder={CERF_DEFAULT_URL} style={inputStyle} />
            <div style={hint}>CERF WhatsApp message URL</div>
          </div>

          <div>
            <label style={label}>Display Name</label>
            <input value={form.display_name}
                   onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                   style={inputStyle} />
            <div style={hint}>Shown in logs and the active-configuration card.</div>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.9rem 0' }}>
          <input type="checkbox" checked={form.is_active}
                 onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          <span style={{ fontSize: '0.85rem', color: '#1f2937' }}>Provider active</span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={save} disabled={saving || loading}
                  style={{ ...btn, background: saving ? '#94a3b8' : '#2563eb', color: '#fff' }}>
            {saving ? 'Saving…' : '💾 Save configuration'}
          </button>
          <button onClick={() => { setTestOpen(o => !o); setTestResult(null); }}
                  style={{ ...btn, background: testOpen ? '#fbbf24' : '#fef3c7', color: '#78350f', border: '1px solid #fde68a' }}>
            {testOpen ? 'Close Test' : 'Send Test'}
          </button>
        </div>
      </div>

      {testOpen && (
        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div style={{ fontWeight: 700, color: '#78350f', fontSize: '0.88rem', marginBottom: '0.6rem' }}>Send a test WhatsApp message</div>
          <div style={{ fontSize: '0.78rem', color: '#78350f', marginBottom: '0.7rem' }}>
            Uses the saved platform credentials and works even while the master switch is off,
            so you can verify the CERF account before enabling the channel.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
            <div>
              <label style={label}>Destination (with country code, no +)</label>
              <input value={testMobile} onChange={e => setTestMobile(e.target.value.replace(/\D/g, '').slice(0, 15))}
                     placeholder="916374970931" style={inputStyle} />
            </div>
            <div>
              <label style={label}>Approved Template ID</label>
              <input value={testTemplateId} onChange={e => setTestTemplateId(e.target.value)}
                     placeholder="2304071377003253" style={{ ...inputStyle, fontFamily: 'monospace' }} />
            </div>
          </div>
          <div style={{ marginTop: '0.6rem' }}>
            <label style={label}>Params (pipe-separated, in template order)</label>
            <input value={testParams} onChange={e => setTestParams(e.target.value)}
                   placeholder="Rahul | Grand Hotel | INV-001" style={inputStyle} />
          </div>
          <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={sendTest} disabled={testRunning}
                    style={{ ...btn, background: testRunning ? '#94a3b8' : '#f59e0b', color: '#fff' }}>
              {testRunning ? 'Sending…' : 'Send Test'}
            </button>
            {testResult && (
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: testResult.success ? '#065f46' : '#991b1b' }}>
                {testResult.success ? '✓ SUBMITTED' : '✗ FAILED'}
                {testResult.http_status ? ` · HTTP ${testResult.http_status}` : ''}
                {testResult.message_id ? ` · id ${testResult.message_id}` : ''}
              </span>
            )}
          </div>
          {testResult && !testResult.success && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#fff', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {testResult.error_message || '—'}
            </div>
          )}
        </div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '0.7rem' }}>Active configuration</div>
        {!existing && <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Not configured yet.</div>}
        {existing && (
          <table style={{ fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Vendor', existing.display_name || existing.provider_name],
                ['Status', existing.is_active ? 'Enabled' : 'Disabled'],
                ['All properties', existing.is_enabled_all_properties ? 'Enabled' : 'Off — nothing will send'],
                ['Api Key', existing.api_token_set ? '(saved)' : '— not set —'],
                ['Source Phone', existing.sender_phone || '— not set —'],
                ['Endpoint', existing.base_url],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={{ padding: '0.3rem 1.5rem 0.3rem 0', color: '#64748b' }}>{k}</td>
                  <td style={{
                    padding: '0.3rem 0', fontWeight: 600,
                    color: (k === 'Status' && existing.is_active) || (k === 'All properties' && existing.is_enabled_all_properties) ? '#059669' : '#1f2937',
                  }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
